import * as i0 from '@angular/core';
import { InjectionToken, isDevMode, Injectable, Inject, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i1 from 'ngx-cookie-service';
import { CookieService } from 'ngx-cookie-service';
import * as i2 from '@wdpr/ra-angular-logger';
import { LoggerModule } from '@wdpr/ra-angular-logger';

const DeviceDetectionConfig = new InjectionToken('device-detection.config');

class DeviceDataService {
    constructor(_cookieService, _loggerService, config) {
        this._cookieService = _cookieService;
        this._loggerService = _loggerService;
        this.config = config;
        this.cookieData = '';
        this.cookieName = this.getConfig();
        this.deviceTypes = {
            DESKTOP: 'desktop',
            MOBILE: 'mobile',
            TABLET: 'tablet',
            DEFAULT: 'unknown'
        };
        this.deviceOS = {
            ANDROID: 'androidos',
            IOS: 'ios',
            DEFAULT: 'unknown'
        };
        this.deviceBrowser = {
            CHROME: 'chrome',
            IEXPLORER: 'microsoft_ie',
            FIREFOX: 'firefox',
            SAFARI: 'safari',
            RA_HYBRID: 'disneyrawebview',
            DEFAULT: 'unknown'
        };
    }
    debugLog(msgToPrint, values) {
        if (isDevMode()) {
            this._loggerService.log(msgToPrint, values);
        }
    }
    getConfig() {
        return this.config.WDPR_DEVICE_DETECTION_COOKIE_NAME;
    }
    getCookie(cookieName, useExternalSourcedValues) {
        if (useExternalSourcedValues) {
            this.debugLog('getCookie reading value from external source: ', useExternalSourcedValues);
            this.cookieData = useExternalSourcedValues;
            return useExternalSourcedValues;
        }
        this.debugLog('getCookie reading cookie ' + cookieName);
        this.cookieData = this._cookieService.get(cookieName);
        this.debugLog('Cookie Data = ', this.cookieData);
        return this.cookieData;
    }
    getParsedCookie() {
        const unencodedString = decodeURI(this.cookieData);
        let jsonString = false;
        try {
            jsonString = JSON.parse(unencodedString);
        }
        catch (e) {
            jsonString = false;
        }
        return {
            obj: jsonString,
            unencodedString: unencodedString
        };
    }
    getDeviceType() {
        let serverDefinedDeviceType = this.deviceTypes.DEFAULT;
        let msg = 'DeviceDataService::getDeviceType() ';
        let jsonObject;
        let parsedValue;
        let parsedCookie;
        if (this.cookieData) {
            parsedCookie = this.getParsedCookie();
            jsonObject = parsedCookie.obj;
            if (!jsonObject || !jsonObject.preferred || !jsonObject.preferred.device) {
                msg += `
                    returned cookie ${this.cookieName} has missing or invalid jsonObject.preferred.device:
                    ${parsedCookie.unencodedString}
                `;
                this.debugLog(msg);
                return serverDefinedDeviceType;
            }
            parsedValue = jsonObject.preferred.device.toLowerCase();
            switch (parsedValue) {
                case this.deviceTypes.DESKTOP:
                case this.deviceTypes.MOBILE:
                case this.deviceTypes.TABLET:
                    serverDefinedDeviceType = parsedValue;
                    break;
                default:
                    msg += `
                        ${this.cookieName} cookie value is not one of the expected values,
                        rejecting whatever it sent and defaulting to desktop for styling:
                        ${jsonObject.preferred.device}
                    `;
                    this.debugLog(msg);
                    break;
            }
        }
        else {
            msg += `
                returned cookie value for ${this.cookieName} was unreadable.
                Defaulting to desktop for styles.
            `;
            this.debugLog(msg);
        }
        msg += 'getDeviceType returning ' + serverDefinedDeviceType;
        this.debugLog(msg);
        return serverDefinedDeviceType;
    }
    getDeviceBrowser() {
        let serverDefinedDeviceBrowser = this.deviceBrowser.DEFAULT;
        let jsonObject;
        let parsedCookie;
        let parsedValue;
        let msg = 'DeviceDataService::getDeviceBrowser(), ';
        if (this.cookieData) {
            parsedCookie = this.getParsedCookie();
            jsonObject = parsedCookie.obj;
            if (!jsonObject || !jsonObject.browserInfo || !jsonObject.browserInfo.agent) {
                msg += `
                    returned ${this.cookieName} cookie has missing or invalid jsonObject.browserInfo.agent
                    ${parsedCookie.unencodedString}
                `;
                this.debugLog(msg);
                return serverDefinedDeviceBrowser;
            }
            parsedValue = jsonObject.browserInfo.agent.toLowerCase();
            switch (parsedValue) {
                case this.deviceBrowser.CHROME:
                case this.deviceBrowser.IEXPLORER:
                case this.deviceBrowser.FIREFOX:
                case this.deviceBrowser.SAFARI:
                case this.deviceBrowser.RA_HYBRID:
                    serverDefinedDeviceBrowser = parsedValue;
                    break;
                default:
                    msg += `
                        ${this.cookieName} cookie value is not one of the expected values,
                        rejecting whatever it sent and defaulting for styling:
                        Passed in: ${parsedValue}
                        Default: ${this.deviceBrowser.DEFAULT}
                    `;
                    serverDefinedDeviceBrowser = this.deviceBrowser.DEFAULT;
                    this.debugLog(msg);
                    break;
            }
        }
        else {
            msg += `
                returned ${this.cookieName} cookie value was unreadable,
                defaulting to browser for styles:
                ${this.deviceBrowser.DEFAULT}
            `;
            this.debugLog(msg);
        }
        msg += 'getDeviceBrowser returning ' + serverDefinedDeviceBrowser;
        this.debugLog(msg);
        return serverDefinedDeviceBrowser;
    }
    getDeviceOS() {
        let serverDefinedDeviceOs = this.deviceOS.DEFAULT;
        let jsonObject;
        let parsedCookie;
        let parsedValue;
        let msg = 'DeviceDataService::getDeviceOS(), ';
        if (this.cookieData) {
            parsedCookie = this.getParsedCookie();
            jsonObject = parsedCookie.obj;
            if (!jsonObject || !jsonObject.deviceInfo || !jsonObject.deviceInfo.os) {
                msg += `
                    returned ${this.cookieName} cookie has missing or invalid data in/for
                    jsonObject.deviceInfo.os:
                    ${parsedCookie.unencodedString}
                `;
                this.debugLog(msg);
                return serverDefinedDeviceOs;
            }
            parsedValue = jsonObject.deviceInfo.os.toLowerCase();
            switch (parsedValue) {
                case this.deviceOS.ANDROID:
                case this.deviceOS.IOS:
                    serverDefinedDeviceOs = parsedValue;
                    break;
                default:
                    msg += `${this.cookieName} cookie value is not one of the expected values,
                        rejecting whatever it sent and defaulting for styling:
                        Passed in: ${parsedValue}
                        Default: ${this.deviceOS.DEFAULT}`;
                    serverDefinedDeviceOs = this.deviceOS.DEFAULT;
                    this.debugLog(msg);
                    break;
            }
        }
        else {
            msg += `
                returned ${this.cookieName} cookie value was unreadable,
                defaulting to OS for styles:
                ${this.deviceOS.DEFAULT}
            `;
            this.debugLog(msg);
        }
        msg += 'getDeviceOS returning ' + serverDefinedDeviceOs;
        this.debugLog(msg);
        return serverDefinedDeviceOs;
    }
    isMobile() {
        return this.getDeviceType() === this.deviceTypes.MOBILE;
    }
    isTablet() {
        return this.getDeviceType() === this.deviceTypes.TABLET;
    }
    isDesktop() {
        return this.getDeviceType() === this.deviceTypes.DESKTOP;
    }
    isAndroid() {
        return this.getDeviceOS() === this.deviceOS.ANDROID;
    }
    isIOS() {
        return this.getDeviceOS() === this.deviceOS.IOS;
    }
    deleteCookie() {
        this._cookieService.delete(this.cookieName);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeviceDataService, deps: [{ token: i1.CookieService }, { token: i2.LoggerService }, { token: 'DEVICE_DETECT_CONFIG' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeviceDataService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeviceDataService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.CookieService }, { type: i2.LoggerService }, { type: DeviceDetectionConfig, decorators: [{
                    type: Inject,
                    args: ['DEVICE_DETECT_CONFIG']
                }] }] });

class DeviceDetectionModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeviceDetectionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: DeviceDetectionModule, imports: [CommonModule,
            LoggerModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeviceDetectionModule, providers: [
            CookieService,
            DeviceDataService,
            {
                provide: 'DEVICE_DETECT_CONFIG',
                useValue: {
                    WDPR_DEVICE_DETECTION_COOKIE_NAME: 'WDPROView',
                }
            }
        ], imports: [CommonModule,
            LoggerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeviceDetectionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        LoggerModule
                    ],
                    providers: [
                        CookieService,
                        DeviceDataService,
                        {
                            provide: 'DEVICE_DETECT_CONFIG',
                            useValue: {
                                WDPR_DEVICE_DETECTION_COOKIE_NAME: 'WDPROView',
                            }
                        }
                    ],
                    declarations: [],
                    exports: []
                }]
        }] });

/*
 * Public API Surface of ra-angular-device-detection
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DeviceDataService, DeviceDetectionModule };
